/**
 * Блок «Представитель» (ТЗ п. 5.2.4).
 *
 * Реквизиты представителя выбираются из справочника «Представители»
 * (решение заказчика 25.10.2026): ИП и организации заказчика одни и те же
 * почти во всех делах. Выбор заполняет наименование, ИНН, адрес, телефон
 * и почту; дальше их можно поправить в деле — справочник от этого
 * не меняется. Реквизиты доверенности у каждого дела свои и не трогаются.
 */

import { useEffect, useState } from 'react';
import type { RepresentativeEntry } from '@bd/domain';
import { api } from '../../../api.js';
import { CheckboxField, Field, InnField, TextField } from '../../../components/fields/index.js';
import { noteDictionaryFailure } from '../../../storage/dictionary-health.js';
import type { BlockProps } from './types.js';

export function RepresentativeBlock({ profile, update, readOnly, errorFor }: BlockProps) {
  const r = profile.representative;
  const set = (patch: Partial<typeof r>) => update((p) => ({ ...p, representative: { ...p.representative, ...patch } }));

  /*
    Справочник есть не во всякой установке (пока только «Чистый Лист»).
    Где он выключен, сервер отвечает UNKNOWN_DICTIONARY — тогда выбора нет
    вовсе, и это не сбой справочника, о котором надо предупреждать.
  */
  const [entries, setEntries] = useState<RepresentativeEntry[] | null>(null);
  useEffect(() => {
    void api
      .dictionary('representatives')
      .then((result) => setEntries(result.entries as never))
      .catch((error: unknown) => {
        if ((error as { code?: string }).code !== 'UNKNOWN_DICTIONARY') noteDictionaryFailure();
        setEntries(null);
      });
  }, []);

  // Ссылка на запись в деле не хранится: выбранная определяется по реквизитам
  const selected = entries?.find((e) => e.name === r.name && (e.inn ?? '') === r.inn)?.id ?? '';

  const pick = (id: string) => {
    const entry = entries?.find((e) => e.id === id);
    if (!entry) return;
    set({
      name: entry.name,
      inn: entry.inn ?? '',
      address: entry.address ?? '',
      phone: entry.phone ?? '',
      email: entry.email ?? '',
    });
  };

  return (
    <section className="block">
      <h2>Представитель</h2>

      <CheckboxField
        label="Заявление подаётся через представителя"
        checked={r.enabled}
        readOnly={readOnly}
        onChange={(v) => set({ enabled: v })}
      />

      {r.enabled && (
        <>
          {entries && <Field
            label="Выбрать из справочника"
            hint={
              entries.length === 0
                ? 'Справочник пуст — администратор заполняет его в разделе «Справочники» → «Представители»'
                : 'Заполнит наименование, ИНН, адрес, телефон и почту. Реквизиты доверенности — ниже, у каждого дела свои'
            }
          >
            <select value={selected} disabled={readOnly || entries.length === 0} onChange={(e) => pick(e.target.value)}>
              <option value="">{selected ? '' : r.name ? '— введён вручную —' : '— выберите представителя —'}</option>
              {entries.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </Field>}

          <div className="row row-2">
            <TextField label="Наименование (ИП или организация)" value={r.name} required readOnly={readOnly} error={errorFor('representative.name')} onChange={(v) => set({ name: v })} />
            <InnField value={r.inn} readOnly={readOnly} onChange={(v) => set({ inn: v })} onResolved={(party) => set({ name: party.name, address: party.address })} />
          </div>

          <TextField label="Адрес" value={r.address} readOnly={readOnly} onChange={(v) => set({ address: v })} />

          <div className="row row-3">
            <TextField label="Телефон" value={r.phone} readOnly={readOnly} onChange={(v) => set({ phone: v })} />
            <TextField label="Электронная почта" value={r.email} readOnly={readOnly} onChange={(v) => set({ email: v })} />
            <TextField
              label="Реквизиты доверенности"
              value={r.powerOfAttorney}
              readOnly={readOnly}
              error={errorFor('representative.powerOfAttorney')}
              hint="Например: от 19.05.2026 № 79 АА 0354009"
              onChange={(v) => set({ powerOfAttorney: v })}
            />
          </div>
        </>
      )}
    </section>
  );
}
